<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import WhatsappLiteBroadcastDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsappLiteCampaign/WhatsappLiteBroadcastDialog.vue';
import WhatsappLiteBroadcastCard from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsappLiteCampaign/WhatsappLiteBroadcastCard.vue';
import WhatsappLiteBroadcastDeleteDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsappLiteCampaign/WhatsappLiteBroadcastDeleteDialog.vue';
import SMSCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/SMSCampaignEmptyState.vue';

const { t } = useI18n();
const store = useStore();

const [showDialog, toggleDialog] = useToggle();
const editingBroadcast = ref(null);
const deletingBroadcast = ref(null);
const confirmDeleteDialogRef = ref(null);

const uiFlags = useMapGetter('evolutionBroadcasts/getUIFlags');
const isFetching = computed(() => uiFlags.value.isFetching);
const broadcasts = useMapGetter('evolutionBroadcasts/getBroadcasts');
const hasNoBroadcasts = computed(
  () => broadcasts.value?.length === 0 && !isFetching.value
);

onMounted(() => {
  store.dispatch('evolutionBroadcasts/get');
  store.dispatch('kanbans/get');
});

const openCreateDialog = () => {
  editingBroadcast.value = null;
  toggleDialog(true);
};

const openEditDialog = broadcast => {
  editingBroadcast.value = broadcast;
  toggleDialog(true);
};

const handleCloseDialog = () => {
  toggleDialog(false);
  editingBroadcast.value = null;
};

const handleActivate = async broadcast => {
  try {
    await store.dispatch('evolutionBroadcasts/activate', broadcast.id);
    useAlert(t('CAMPAIGN.WHATSAPP_LITE.CARD.ACTIONS.ACTIVATE_SUCCESS'));
  } catch (error) {
    useAlert(t('CAMPAIGN.WHATSAPP_LITE.CARD.ACTIONS.ACTIVATE_ERROR'));
  }
};

const handlePause = async broadcast => {
  try {
    await store.dispatch('evolutionBroadcasts/pause', broadcast.id);
    useAlert(t('CAMPAIGN.WHATSAPP_LITE.CARD.ACTIONS.PAUSE_SUCCESS'));
  } catch (error) {
    useAlert(t('CAMPAIGN.WHATSAPP_LITE.CARD.ACTIONS.PAUSE_ERROR'));
  }
};

const handleDeleteClick = broadcast => {
  deletingBroadcast.value = broadcast;
  confirmDeleteDialogRef.value.dialogRef.open();
};
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.WHATSAPP_LITE.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.WHATSAPP_LITE.NEW_CAMPAIGN')"
    @click="openCreateDialog"
    @close="handleCloseDialog"
  >
    <template #action>
      <WhatsappLiteBroadcastDialog
        v-if="showDialog"
        :broadcast="editingBroadcast"
        @close="handleCloseDialog"
      />
    </template>
    <div
      v-if="isFetching"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <div v-else-if="!hasNoBroadcasts" class="flex flex-col gap-4">
      <WhatsappLiteBroadcastCard
        v-for="broadcast in broadcasts"
        :key="broadcast.id"
        :broadcast="broadcast"
        @edit="openEditDialog(broadcast)"
        @delete="handleDeleteClick(broadcast)"
        @activate="handleActivate(broadcast)"
        @pause="handlePause(broadcast)"
      />
    </div>
    <SMSCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.WHATSAPP_LITE.EMPTY_STATE.TITLE')"
      :subtitle="t('CAMPAIGN.WHATSAPP_LITE.EMPTY_STATE.SUBTITLE')"
      class="pt-14"
    />
    <WhatsappLiteBroadcastDeleteDialog
      ref="confirmDeleteDialogRef"
      :selected-broadcast="deletingBroadcast"
    />
  </CampaignLayout>
</template>
